import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../..";
import { MenuKeysType, MENU_MODULES } from "src/constants";
import { Theme } from "./client.type";
import { MenuItemType, persistedModules, persistedTheme } from "../../persistedClientData";

export type UserDetailsType = {
  isSucceeded: boolean;
  message: string | null;
  userId: string;
  phoneNumber: string;
  totalPointsBalance: number;
  totalValue: number;
  redeemFactor: number;
  email: string | null;
  segment: string;
  nationality: string;
};

interface ClientState {
  selectedMenu: MenuKeysType | undefined;
  selectedClientId: string | undefined;
  menuItems: MenuItemType[];
  userDetails: UserDetailsType;
  isUserFetching:boolean;
  clientTheme:Theme;
}

export const defaultUserDetails: UserDetailsType = {
  isSucceeded: false,
  message: "",
  userId: "",
  phoneNumber: "",
  totalPointsBalance: 0,
  totalValue: 0,
  redeemFactor: 1,
  email: "",
  segment: "",
  nationality: "",
};

export const defaultTheme:Theme = {
  primaryColor:"#0071CEFF",
  secondaryColor:"#F47920FF",
}

const menuOrderMap: Record<MenuKeysType, number> = MENU_MODULES.reduce(
  (acc, module) => {
    acc[module.type as MenuKeysType] = module.order;
    return acc;
  },
  {} as Record<MenuKeysType, number>,
);

function deriveSelectedMenu(items: MenuItemType[]) {
  const allowedItems = items.filter((item) => item.type in menuOrderMap);
  if (!allowedItems.length) return { selectedMenu: undefined, selectedClientId: undefined };
  const top = allowedItems.reduce((prev, curr) => (menuOrderMap[curr.type] < menuOrderMap[prev.type] ? curr : prev));
  return { selectedMenu: top.type, selectedClientId: top.clientId };
}

const persistedMenuItems = persistedModules.read() ?? [];

const initialState: ClientState = {
  ...deriveSelectedMenu(persistedMenuItems),
  menuItems: persistedMenuItems,
  userDetails: defaultUserDetails,
  isUserFetching:true,
  clientTheme: persistedTheme.read() ?? defaultTheme,
};

const clientSlice = createSlice({
  name: "clientSlice",
  initialState,
  reducers: {
    setMenuItems: (state, action: PayloadAction<MenuItemType[]>) => {
      state.menuItems = action.payload;
      persistedModules.write(action.payload);
      const { selectedMenu, selectedClientId } = deriveSelectedMenu(action.payload);
      state.selectedMenu = selectedMenu;
      state.selectedClientId = selectedClientId;
    },
    setSelectedMenu: (state, action: PayloadAction<MenuKeysType>) => {
      state.selectedMenu = action.payload;
    },
    setClientId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedClientId = action.payload;
    },
    clearSelectedMenu: (state) => {
      state.selectedMenu = undefined;
      state.selectedClientId = undefined;
    },
    setUserDetails: (state, action: PayloadAction<UserDetailsType>) => {
      state.userDetails = action.payload;
    },
    setIsUserFetching:(state, action:PayloadAction<boolean>)=>{
      state.isUserFetching = action.payload;
    },
    setClientTheme:(state, action:PayloadAction<Theme>) =>{
      state.clientTheme = action.payload;
      persistedTheme.write(action.payload);
    }
  },
});

export const { setMenuItems, setSelectedMenu, setClientId, clearSelectedMenu, setUserDetails, setIsUserFetching, setClientTheme } = clientSlice.actions;
export const getSelectedMenu = (state: RootState) => state.client.selectedMenu;
export const getSelectedClientId = (state: RootState) => state.client.selectedClientId;
export const getUserDetails = (state: RootState) => state.client?.userDetails;
export const getIsUserFetching = (state:RootState)=>state.client.isUserFetching;
export const getClientTheme = (state:RootState)=>state.client.clientTheme;

export default clientSlice.reducer;
